import { FC, useState } from 'react';

import {
  Button,
  ButtonDropdown,
  Input,
  InputIcon,
  Tabs,
  type TDropdownItem,
  H3,
  Text,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';

import inboundEmailKeys from '@/api/queries/inbound-emails/keyFactory';
import useGetInboundEmailInbox from '@/api/queries/inbound-emails/useGetInboundEmailInbox';
import useGetMyInboundEmailAddresses from '@/api/queries/inbound-emails/useGetMyInboundEmailAddresses';
import { InboundEmailInboxFilter, SortDirection } from '@/gql/graphql';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';

import { clstx } from '@payaca/utilities/cvaMerge';
import InboxEmailList from './InboxEmailList';

export type InboxListPanelProps = {
  selectedEmailId: string | null;
  onSelectEmail: (emailId: string | null) => void;
  searchTerm: string;
  onChangeSearchTerm: (searchTerm: string) => void;
  sortDirection: SortDirection;
  onChangeSortDirection: (direction: SortDirection) => void;
};

const TAB_FILTERS: {
  label: string;
  filter: InboundEmailInboxFilter | undefined;
}[] = [
  { label: 'Unassigned', filter: 'UNASSIGNED' },
  { label: 'Assigned', filter: 'ASSIGNED' },
  { label: 'Archived', filter: 'ARCHIVED' },
  { label: 'All', filter: undefined },
];

const InboxListPanel: FC<InboxListPanelProps> = (props) => {
  const {
    selectedEmailId,
    onSelectEmail,
    searchTerm,
    onChangeSearchTerm,
    sortDirection,
    onChangeSortDirection,
  } = props;

  const queryClient = useQueryClient();
  const { handleCopy } = useCopyToClipboard();
  const { data: addressesData } = useGetMyInboundEmailAddresses();

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const activeFilter = TAB_FILTERS[activeTabIndex]?.filter;

  const inboxResult = useGetInboundEmailInbox(
    { offset: 0, limit: 20 },
    {
      filter: activeFilter,
      searchTerm: searchTerm || undefined,
      sortDirection,
    }
  );

  const sortItems: TDropdownItem[] = [
    {
      label: 'Newest first',
      selected: sortDirection === 'DESC',
      onClick: () => onChangeSortDirection('DESC'),
    },
    {
      label: 'Oldest first',
      selected: sortDirection === 'ASC',
      onClick: () => onChangeSortDirection('ASC'),
    },
  ];

  const handleRefresh = () => {
    void queryClient.invalidateQueries({
      queryKey: inboundEmailKeys.inbox(),
    });
  };

  const forwardAddress = addressesData?.me.inboundEmailAddresses.forwardAddress;
  const copyAddress = addressesData?.me.inboundEmailAddresses.copyAddress;
  const totalCount =
    inboxResult.data?.pages?.[0].me.inboundEmailInbox.totalCount;

  return (
    <div className="flex max-h-screen w-full @screen-md:w-xl @screen-md:min-w-xl flex-col bg-gray-50 overflow-y-auto">
      {/* Header */}
      <div className="space-y-3 p-4 sticky top-0 bg-gray-50 z-10">
        <div className="flex items-start justify-between">
          <div>
            <H3>Inbox</H3>
            <Text level="supporting">{totalCount} messages</Text>
          </div>
          <div className="flex items-center gap-2.5">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              aria-label="Refresh inbox"
              className="h-full aspect-square"
            >
              <UntitledIcon
                name="refresh-cw-01"
                className={clstx(
                  'size-4',
                  inboxResult.isFetching && 'animate-spin'
                )}
              />
            </Button>
            <ButtonDropdown
              className="h-full aspect-square"
              dropdownOptions={{
                items: [
                  {
                    label: (
                      <>
                        <UntitledIcon name="copy-07.3" className="size-4" />
                        Copy forward address
                      </>
                    ),
                    onClick: () => void handleCopy(forwardAddress ?? ''),
                    disabled: !forwardAddress,
                  },
                  {
                    label: (
                      <>
                        <UntitledIcon name="copy-07.3" className="size-4" />
                        Copy Bcc address
                      </>
                    ),
                    onClick: () => void handleCopy(copyAddress ?? ''),
                    disabled: !copyAddress,
                  },
                ],
                anchor: 'start',
              }}
              size="sm"
            >
              <UntitledIcon name="edit-05.3" className="size-4" />
            </ButtonDropdown>
          </div>
        </div>

        {/* Search */}
        <Input
          sizeVariant="sm"
          placeholder="Search by sender..."
          value={localSearchTerm}
          onChange={setLocalSearchTerm}
          onChangeTimeout={onChangeSearchTerm}
          changeTimeoutMs={300}
          leadingElement={<InputIcon name="search-lg.3" />}
        />
      </div>
      <div className="p-4 pt-0 space-y-3">
        <Tabs
          variant="segment"
          fullWidth
          className="overflow-auto w-full"
          classNameTabPanels="m-0"
          selectedIndex={activeTabIndex}
          onChange={setActiveTabIndex}
        >
          {TAB_FILTERS.map((tab) => (
            <Tabs.Panel
              key={tab.label}
              label={tab.label}
              render={() => (
                <>
                  {/* Sort */}
                  <div className="text-right mb-2 -mt-1">
                    <ButtonDropdown
                      dropdownOptions={{ items: sortItems, anchor: 'start' }}
                      variant="quaternary"
                      size="xs"
                      aria-label="Change sort order"
                    >
                      {sortItems.filter((item) => item.selected)[0]?.label}{' '}
                      <UntitledIcon
                        name="switch-vertical-01.3"
                        className="size-3.5"
                      />
                    </ButtonDropdown>
                  </div>

                  <InboxEmailList
                    inboxResult={inboxResult}
                    selectedEmailId={selectedEmailId}
                    onSelectEmail={onSelectEmail}
                  />
                </>
              )}
            />
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default InboxListPanel;
